import styled from "@emotion/styled";
import { ValidationErrors } from "final-form";
import arrayMutators from "final-form-arrays";
import { isEqual, omit } from "lodash";
import { FC, useState } from "react";
import { Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router";

import { ErrorFilled } from "@livechat/design-system-icons";
import { DesignToken, Icon, SpacingToken, Tab, TabsList, TabsWrapper } from "@livechat/design-system-react-components";

import { ErrorBoundary } from "components/error-boundary";
import { RememberUnmountedFields } from "components/final-form/RememberUnmountedFields";
import { FixedSaveBar, SaveBarMode } from "components/form";
import { Grid, ViewStack } from "components/grid";
import { ChangesNotSavedModal } from "components/modals/ChangesNotSavedModal";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { useAgentFlag } from "hooks/use-agent-flag";
import FullPageViewContainer from "routes/settings/components/FullPageViewContainer";
import { SettingsRoutes } from "routes/settings/constants";
import { ContactFormActions } from "store/entities/contact-forms/actions";
import { IContactForm } from "store/entities/contact-forms/interfaces";
import { selectContactForms } from "store/entities/contact-forms/selectors";
import { selectDefaultTeamId } from "store/entities/license";
import { withVar } from "themes/helpers";

import { ConfirmContactFormChangesModal } from "../ConfirmContactFormChangesModal";

import { ContactFormPreview } from "./ContactFormPreview";
import { Customize } from "./Customize";
import { General } from "./General";
import { Publish } from "./Publish";
import { validate } from "./blocks/validators";
import { BLOCK_DEFAULTS, FORM_BUILDER_TABS, TABS } from "./constants";
import { hasTruthyValue } from "./helpers";

type Mode = "create" | "update";

export const FormBuilder: FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();
  const params = useParams<{ id: string }>();
  const duplicateForm = pathname.startsWith(`${SettingsRoutes.ContactForms}/duplicate`);
  const [selectedTab, setSelectedTab] = useState<string>(FORM_BUILDER_TABS.GENERAL);
  const [changesNotSavedModalOpen, setChangesNotSavedModalOpen] = useState(false);
  const [saveModalShown, toggleSaveModalShown] = useState(false);
  const defaultTeamId = useSelector(selectDefaultTeamId);
  const contactForms = useSelector(selectContactForms);
  const [confirmChangesDisabled, toggleConfirmChangesDisabled] = useAgentFlag("confirmContactFormChangesDisabled");
  const mode: Mode = params.id ? "update" : "create";
  const contactForm =
    mode === "create"
      ? {
          teamID: defaultTeamId,
          version: 2,
          fields: [
            BLOCK_DEFAULTS.header,
            BLOCK_DEFAULTS.name,
            BLOCK_DEFAULTS.email,
            BLOCK_DEFAULTS.subject,
            BLOCK_DEFAULTS.textarea,
            BLOCK_DEFAULTS.submit,
            BLOCK_DEFAULTS.options,
          ],
        }
      : {
          ...omit(contactForms[params.id], "updatedAt"),
          name: duplicateForm ? `Copy of ${contactForms[params.id].name}` : contactForms[params.id]?.name,
        };

  const handleBack = (pristine: boolean): void => {
    if (!pristine) {
      setChangesNotSavedModalOpen(true);
    } else {
      history.push(SettingsRoutes.ContactForms);
    }
  };

  const handleSubmitForm = (value: IContactForm): void => {
    if (mode === "create" || duplicateForm) {
      dispatch(ContactFormActions.create({ value }));
    } else {
      dispatch(ContactFormActions.update({ value }));
    }
    trackEngagement(`Contact form ${mode === "create" ? "created" : "updated"}`, EventPlace.Settings, {
      fields: value.fields?.map(field => field.type).filter(type => type !== "submit" && type !== "options"),
    });
  };

  const handleSave = (value: IContactForm): void => {
    if (confirmChangesDisabled || mode === "create" || duplicateForm) {
      handleSubmitForm(value);
    } else {
      toggleSaveModalShown(true);
    }
  };

  const handleDiscardChanges = (): void => {
    setChangesNotSavedModalOpen(false);
    history.push(SettingsRoutes.ContactForms);
  };

  const handleSaveModalClose = (): void => {
    toggleSaveModalShown(false);
  };

  const hasError = (tabId: string, errors: ValidationErrors, touched?: Record<string, boolean>): boolean => {
    if (selectedTab !== FORM_BUILDER_TABS.GENERAL && tabId == FORM_BUILDER_TABS.GENERAL) {
      return Boolean(errors?.name);
    }

    if (tabId === FORM_BUILDER_TABS.GENERAL) {
      return Boolean(errors?.name && touched?.name);
    }

    if (tabId === FORM_BUILDER_TABS.CUSTOMIZE) {
      const customizeErrors = omit(errors, "name");
      return hasTruthyValue(customizeErrors);
    }
    return false;
  };

  const handleTabChange = (tab: string): void => {
    trackEngagement("Settings: Contact form tabs navigation used", EventPlace.Settings, {
      tabPage: tab,
    });
    setSelectedTab(tab);
  };

  return (
    <ErrorBoundary
      onError={() => {
        history.push(SettingsRoutes.ContactForms);
      }}
    >
      <Form<IContactForm>
        onSubmit={handleSave}
        initialValues={contactForm}
        mutators={{ ...arrayMutators }}
        keepDirtyOnReinitialize
        keepTouched
        destroyOnUnregister={false}
        validate={validate}
      >
        {({ errors, touched, values }) => {
          const pristine = isEqual(values, contactForm);
          return (
            <FullPageViewContainer
              title={`${mode === "create" || duplicateForm ? "New" : "Edit"} contact form`}
              onBack={() => handleBack(pristine)}
            >
              <RememberUnmountedFields />
              <Grid relativeWidths={[1, 1]} minWidths={[320, 0]}>
                <StyledViewStack css={{ scrollbarGutter: "stable", position: "relative" }}>
                  <form
                    onSubmit={e => e.preventDefault()}
                    css={{ paddingBottom: pristine ? undefined : withVar(SpacingToken.Spacing12) }}
                  >
                    <TabsWrapper
                      css={{
                        paddingLeft: withVar(SpacingToken.Spacing4),
                        paddingRight: withVar(SpacingToken.Spacing4),
                        backgroundColor: withVar(DesignToken.SurfacePrimaryDefault),
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      <TabsList>
                        {TABS.map(({ id, title }) => (
                          <Tab
                            data-testid={`${id}-tab`}
                            key={id}
                            isSelected={selectedTab === id}
                            onClick={() => handleTabChange(id)}
                            size="large"
                            icon={
                              hasError(id, errors, touched) ? (
                                <Icon
                                  source={ErrorFilled}
                                  customColor={withVar(DesignToken.ContentBasicNegative)}
                                  data-testid="error-icon"
                                />
                              ) : undefined
                            }
                          >
                            {title}
                          </Tab>
                        ))}
                      </TabsList>
                    </TabsWrapper>
                    {selectedTab === FORM_BUILDER_TABS.GENERAL && <General />}
                    {selectedTab === FORM_BUILDER_TABS.PUBLISH && <Publish />}
                    {selectedTab === FORM_BUILDER_TABS.CUSTOMIZE && <Customize />}
                  </form>
                </StyledViewStack>
                <StyledViewStack>
                  <ContactFormPreview contactForm={values} />
                </StyledViewStack>
                <ChangesNotSavedModal
                  isOpen={changesNotSavedModalOpen}
                  onDiscardChanges={handleDiscardChanges}
                  onBackToEditing={() => setChangesNotSavedModalOpen(false)}
                >
                  You have unsaved changes in the contact form. They&apos;ll be lost if you close the contact form
                  editor without saving them.
                </ChangesNotSavedModal>
                {saveModalShown && (
                  <ConfirmContactFormChangesModal
                    onClose={handleSaveModalClose}
                    onSubmit={() => handleSubmitForm(values)}
                    onConfirmationDisableToggle={toggleConfirmChangesDisabled}
                  />
                )}
                <FixedSaveBar
                  saveTooltip="Fill required fields"
                  onSave={() => handleSave(values)}
                  saveDisabled={hasTruthyValue(errors)}
                  onCancel={() => handleBack(pristine)}
                  customSaveLabel={`${mode === "create" || duplicateForm ? "Create" : "Update"} contact form`}
                  mode={pristine ? SaveBarMode.Hidden : SaveBarMode.OfferSave}
                />
              </Grid>
            </FullPageViewContainer>
          );
        }}
      </Form>
    </ErrorBoundary>
  );
};

const StyledViewStack = styled(ViewStack)`
  overflow: auto;
  height: 100%;
`;
